import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { User } from '@supabase/supabase-js';
import storage from 'local-storage-fallback';
import '@/styles/EhAuthDialog.css';

import { Dialog, DialogContent, DialogDescription, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from './ui/input-otp';
import { Button } from './ui/button';
import { getRandomSampleModel, SampleModelAsset } from '../utils/sampleModels';
import { supabase, supabaseEh } from '../lib/supabase';
import EhPostSignupDialog from '../components/EhPostSignupDialog';
import { LS_ONBOARDING_V1_KEY } from '../lib/constants';
import { validateEmail } from '@/lib/utils';
import { IOS, isAppleDevice, isFromAppStores, isStoreDemoAccount } from '@/lib/detection';
import { EHUserDetailsMeta } from '@/lib/types';

import { useUserInfo } from '@/hooks/useUserInfo';
import { useTracking, USER_EVENTS } from '@/hooks/useTracking';

interface EhAuthDialogProps {
  open: boolean;
  onOpenChange: (open: boolean, {user, email, onboardingCompleted}: {
    user?: User,
    email?: string,
    onboardingCompleted?: boolean
  }) => void;
  onOnboardingOpenChange?: (open: boolean, { user, onboardingCompleted }: {
    user?: User,
    onboardingCompleted?: boolean
  }) => void;
  // Should only be enabled on one instance at a time
  // TODO: refactor into a hook to avoid conflicts between instances
  enableOnboardingAutoCheck?: boolean;
  activeFilters?: Partial<Pick<SampleModelAsset, 'gender' | 'style' | 'ethnicity'>>;
  onValidOtp?: () => void;
  newUserMetadata?: Record<string, string | number | boolean>;
  mediaUrl?: string;
  ref?: React.RefObject<{ checkOnboarding: () => Promise<boolean> }>;
}

const OTP_LENGTH = 6;

const EhAuthDialog: React.FC<EhAuthDialogProps> = forwardRef(function EhAuthDialog({
  open,
  onOpenChange,
  onOnboardingOpenChange,
  enableOnboardingAutoCheck,
  activeFilters,
  onValidOtp = () => {},
  newUserMetadata = {},
  mediaUrl
}, ref) {
  const { isPastUser, user } = useUserInfo();
  const { trackEvent } = useTracking();

  const [step, setStep] = useState<'email' | 'otp'>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState<SampleModelAsset | undefined>();
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [_, setCheckingOnboarding] = useState(false);
  const [skipUserDetails, setSkipUserDetails] = useState(false);

  const isOnboardingCompletedRef = useRef(false);
  const hasClosedOnboardingRef = useRef(false);

  const showApple = isAppleDevice();
  const showGoogle = !isAppleDevice();
  const showDiscord = true;

  // Pick a random image when dialog opens or filters change
  useEffect(() => {
    if (open) {
      setSelectedImage(getRandomSampleModel({
        ...activeFilters,
        ...(isFromAppStores() ? { isSafeForStores: true } : {})
      }));
      setStep('email');
      setEmail('');
      setOtp('');
      setError('');
    }
  }, [open, activeFilters]);
  
  useEffect(() => {
    // No gender and no nickname are ok, only second step is required
    if (onboardingOpen && hasClosedOnboardingRef.current) setSkipUserDetails(true);
  }, [onboardingOpen]);

  // Email OTP logic (Supabase)
  const handleEmailSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    // Skip to password authentication for demo accounts
    if (STORE_DEMO_ACCOUNTS_ENABLED && isStoreDemoAccount(email)) {
      setStep('otp');
      return;
    }

    setLoading(true);
    try {
      const data: Record<string, string | number | boolean> = { ...newUserMetadata };
      const l = window.location;
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          data,
          emailRedirectTo: `${l.origin + l.pathname}`
        }
      });
      if (error) throw error;
      setStep('otp');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send code.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (v?: string) => {
    setLoading(true);
    setError('');
    try {
      const { data, error } = await supabase.auth.verifyOtp({
        email,
        token: v || otp,
        type: 'email',
      });
      if (error) throw error;
      if (!data.user) throw new Error('No user returned');
      onValidOtp();

      const oneMinuteAgo = new Date(Date.now() - 60 * 1000);
      const existingUser = new Date(data.user.created_at) < oneMinuteAgo;
      if (existingUser) {
         // wait for checkOnboarding if network call needs to be made (no localstorage key).
         // If it's not enough delay, we just risk showing the onboarding dialog
         // or not sending the pendingMessage in ChatPage
        await new Promise(resolve => setTimeout(resolve, 600));
      }

      const onboardingCompleted = isOnboardingCompletedRef.current;
      if (import.meta.env.DEV) console.log('[DEV] onboardingCompleted', onboardingCompleted);

      onOpenChange(false, { user: data.user, email, onboardingCompleted });
      setEmail('');
      setOtp('');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      let errorMessage = err?.message || 'Invalid or expired code.';
      if (err?.code === 'otp_expired') {
        errorMessage = 'This code has expired or is invalid. Please request a new one.';
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const STORE_DEMO_ACCOUNTS_ENABLED = import.meta.env.VITE_STORE_DEMO_ACCOUNTS_ENABLED === 'true';

  /** For demo accounts */
  const handleHiddenPasswordSubmit = async (v?: string) => {
    setLoading(true);
    setError('');
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password: v || otp
      });
      if (error) throw error;
      if (!data.user) throw new Error('No user returned');
      onValidOtp();

      trackEvent(USER_EVENTS.USER_DEMO_ACCOUNT_LOGIN, { email });

      // Skip onboarding
      onOpenChange(false, { user: data.user, email, onboardingCompleted: true });
      setEmail('');
      setOtp('');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      let errorMessage = err?.message || 'Invalid password';
      if (err?.code === 'otp_expired') {
        errorMessage = 'This code has expired or is invalid. Please request a new one.';
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Social login handlers (Supabase)
  const handleSocialLogin = async (provider: 'apple' | 'google' | 'discord') => {
    setLoading(true);
    setError('');
    try {
      const l = window.location;
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${l.origin + l.pathname}`
        }
      });
      if (error) throw error;
      onOpenChange(false, {});
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Social login failed.');
    } finally {
      setLoading(false);
    }
  };

  const checkOnboarding = useCallback(async () => {
    const user = (await supabase.auth.getSession()).data.session?.user;
    if (!user) return false;

    if (import.meta.env.DEV) console.log('[DEV] checking onboarding', user.id);

    const onboardingV1 = JSON.parse(storage.getItem(LS_ONBOARDING_V1_KEY) || '{}');
    if (onboardingV1.uid === user.id) {
      isOnboardingCompletedRef.current = true;
      return true;
    }

    setCheckingOnboarding(true);
    return supabaseEh
      .from('user_details')
      .select('eh_usr_meta')
      .eq('user_id', user.id)
      .maybeSingle()
      .then(({ data, error }) => {
        setCheckingOnboarding(false);
        if (error || !data) {
          if (import.meta.env.DEV) console.log('[DEV] error or no data when checking onboarding', error, data);
          setOnboardingOpen(true);
        } else {
          const meta = data.eh_usr_meta as EHUserDetailsMeta ?? {};
          // hasSetDetailsV1At may have been overwritten by onboardingV1completedAt in the past
          setSkipUserDetails(prev => prev || Boolean(meta.hasSetDetailsV1At || meta.onboardingV1completedAt));
          if (!meta.onboardingV1completedAt) {
            setOnboardingOpen(true);
          } else {
            storage.setItem(LS_ONBOARDING_V1_KEY, JSON.stringify({
              at: Date.now().toString(),
              uid: user.id
            }));
            setOnboardingOpen(false);
            isOnboardingCompletedRef.current = true;
            return true;
          }
        }
        return false;
      });
  }, []);
  useImperativeHandle(ref, () => ({ checkOnboarding }));

  // Check onboarding after login
  useEffect(() => {
    if (!user) {
      setOnboardingOpen(false);
      return;
    }
    if (enableOnboardingAutoCheck) {
      checkOnboarding();
    }
  }, [checkOnboarding, enableOnboardingAutoCheck, user]);

  // Determine which image to show
  const leftImageUrl = mediaUrl || (selectedImage ? `/${selectedImage.assetPath}` : undefined);

  return (
    <>
    <Dialog open={open} onOpenChange={(open) => onOpenChange(open, {})}>
      <DialogContent
        className="sm:max-w-4xl p-0 flex flex-col md:flex-row gap-0 bg-zinc-900 border-none text-white"
        onOpenAutoFocus={(e) => { // see comment about <Input> keyboard below
          if (IOS) e.preventDefault();
        }}
      >
        {/* Left: Model image (hidden on small screens) */}
        <div
          className="eh-auth-dialog--minimized w-full md:flex md:max-w-1/2 aspect-[13/19] overflow-hidden items-center justify-center bg-zinc-800 md:rounded-l-lg"
        >
          {leftImageUrl && (
            <img
              src={leftImageUrl}
              alt="Sample model"
              className="object-cover object-[50%_25%] scale-110 md:rounded-l-lg h-full w-full pointer-events-none"
            />
          )}
        </div>
        {/* Right: Auth form */}
        <div className="flex-1 px-6 pb-6 pt-3 sm:pt-6 flex flex-col justify-center">
          <DialogTitle className="eh-auth-dialog--minimized text-xl sm:text-2xl font-bold sm:mb-2">
            {step === 'otp' ? `Enter code sent to ${email}` : (isPastUser ? 'Sign in to EveningHoney' : 'Get your free account')}
          </DialogTitle>
          <DialogDescription className="mb-2 sm:mb-4 text-white/80 text-md">
            {step === 'otp'
              ? <span>Please check your email inbox and enter the code we just sent you to proceed.</span>
              : <span>
                  <span className={`font-medium sm:font-bold text-lg text-white sm:mb-2 inline-block ${isPastUser ? '' : ''}`}>
                    <span className="sm:hidden">First messages & images on us!</span>
                    <span className="hidden sm:inline">First messages and images are on us.</span>
                  </span>
                  <br className={`${isPastUser ? '' : ''}`} />
                  <span className="text-sm">Receive a one-time code by email or use your Google account to sign in.</span>
                </span>}
          </DialogDescription>
          {step === 'email' && (
            <form onSubmit={handleEmailSubmit}>
              <Input
                type="email"
                placeholder={`Enter your email${!showGoogle ? ' or Gmail address' : ''}`}
                value={email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                className="mb-3 focus-visible:border-white focus-visible:ring-white/50"
                // autofocus does not scroll the input into view on iOS
                // and keyboard height is not deduced from viewport height so that the input is out of view
                autoFocus={!IOS}
              />
              <Button
                type="submit"
                disabled={!email || loading || !validateEmail(email)}
                className="w-full bg-pink-700 hover:bg-pink-600 text-white text-lg font-bold shadow"
              >
                {loading ? 'Sending...' : 'Continue'}
              </Button>
            </form>
          )}
          {step === 'otp' && (
            <>
              <div className="flex flex-col items-center justify-center my-6">
                <InputOTP
                  maxLength={OTP_LENGTH}
                  value={otp}
                  onChange={val => {
                    const code = val.replace(/[^0-9]/g, '');
                    setOtp(code);
                    if (code.length === OTP_LENGTH) {
                      if (STORE_DEMO_ACCOUNTS_ENABLED && isStoreDemoAccount(email)) handleHiddenPasswordSubmit(code);
                      else handleOtpSubmit(code);
                    }
                  }}
                  disabled={loading}
                  className="mb-3 text-4xl justify-center gap-3"
                  autoFocus
                >
                  <InputOTPGroup>
                    {Array.from({ length: OTP_LENGTH }).map((_, i) => (
                      <InputOTPSlot key={i} index={i} />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <Button
                onClick={() => {
                  if (STORE_DEMO_ACCOUNTS_ENABLED && isStoreDemoAccount(email)) handleHiddenPasswordSubmit();
                  else handleOtpSubmit();
                }}
                disabled={otp.length < OTP_LENGTH || loading}
                className="w-full mb-3 bg-pink-700 hover:bg-pink-600 text-white text-lg font-bold shadow"
              >
                <span>{loading ? 'Verifying...' : 'Log In'}</span>
                <span
                  className={loading
                    ? 'w-5 h-5 border-2 border-white border-t-pink-500 rounded-full animate-spin'
                    : 'hidden'}
                />
              </Button>
              <Button
                variant="ghost"
                className="mb-3 w-fit mx-auto"
                onClick={() => setStep('email')}
                disabled={loading}
              >
                Back
              </Button>
            </>
          )}
          {/* OR Separator and OAuth only in email step */}
          {step === 'email' && (showApple || showGoogle || showDiscord) && (
            <>
              <div className="flex items-center my-2 md:my-4 select-none">
                <div className="flex-grow border-t border-zinc-700" />
                <span className="mx-4 text-zinc-400 font-semibold">OR</span>
                <div className="flex-grow border-t border-zinc-700" />
              </div>
              <div className="flex flex-col gap-2">
                {showApple && (
                  <Button
                    size="lg"
                    className="w-full bg-white text-black hover:bg-white hover:text-black select-none text-md gap-0"
                    onClick={() => handleSocialLogin('apple')}
                    disabled={loading}
                  >
                    <img src="/logos/apple-logo-siwa-left-large.svg" alt="Apple" className="h-full py-px mr-1" />
                    <span>Sign in with Apple</span>
                  </Button>
                )}
                {showGoogle && (
                  <Button
                    size="lg"
                    className="w-full bg-white text-zinc-700 hover:bg-zinc-100 hover:text-zinc-800 select-none text-md"
                    onClick={() => handleSocialLogin('google')}
                    disabled={loading}
                  >
                    <img src="/logos/google-logo.svg" alt="Google" className="size-5 mr-1" />
                    <span>Sign in with Google</span>
                  </Button>
                )}
                {showDiscord && (
                  <Button
                    size="lg"
                    className="w-full bg-[#5865F2] hover:bg-[#5865F2]/90 text-white hover:text-white select-none text-md"
                    style={{ border: 'none' }}
                    onClick={() => handleSocialLogin('discord')}
                    disabled={loading}
                  >
                    <img src="/logos/discord-logo-white.svg" alt="Discord" className="size-5 mr-1" />
                    <span>Sign in with Discord</span>
                  </Button>
                )}
              </div>
            </>
          )}
          {error && <div className="mt-3 text-red-500 text-sm">{error}</div>}
        </div>
      </DialogContent>
    </Dialog>
    {!!user && (
      <EhPostSignupDialog
        open={onboardingOpen}
        onClose={({ onboardingCompleted }) => {
          setOnboardingOpen(false);
          onOnboardingOpenChange?.(false, { user, onboardingCompleted });
          hasClosedOnboardingRef.current = true;
        }}
        user={user}
        forceStep={skipUserDetails ? 2 : 1}
      />
    )}
    </>
  );
});

export default EhAuthDialog; 